import { useState, useEffect } from 'react';
import {
    Package, Tag, Euro, Scale, Ruler, MapPin, Link, Building, User,
    Plus, X, Sparkles, Loader2, Upload, ArrowLeft, Save, Image
} from 'lucide-react';
import { Button, Modal, AddressAutocomplete } from '../ui';
import {
    getProductTypes,
    createProduct,
    updateProduct,
    addProductPhoto,
    removeProductPhoto,
    setMainPhoto,
    LISTING_TYPE,
    PRODUCT_CONDITION,
    PICKUP_TYPE,
    OWNER_TYPE
} from '../../services/productsService';
import { uploadFile, getFileUrl } from '../../services/filesService';
import { enhanceImageWithAI } from '../../services/geminiService';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Product Form Component
 * Create or edit a product
 */
const ProductForm = ({
    product = null,
    onSave,
    onCancel,
    userPlaces = [] // User's businesses/places
}) => {
    const { language, t } = useLanguage();
    const tp = t.products || {};
    const isEditMode = !!product;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [productTypes, setProductTypes] = useState([]);
    const [photos, setPhotos] = useState(product?.photos || []);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [enhancingPhoto, setEnhancingPhoto] = useState(null);
    const [showEnhanceModal, setShowEnhanceModal] = useState(null); // index of photo to enhance
    const [enhancePrompt, setEnhancePrompt] = useState('');

    const [formData, setFormData] = useState({
        title: product?.title || '',
        description: product?.description || '',
        typeId: product?.typeId || product?.typeid || '',
        listingType: product?.listingType || product?.listingtype || LISTING_TYPE.SELL,
        price: product?.price || '',
        currency: product?.currency || 'EUR',
        priceNegotiable: product?.priceNegotiable || product?.pricenegotiable || false,
        condition: product?.condition || PRODUCT_CONDITION.USED,
        quantity: product?.quantity || 1,
        weight: product?.weight || '',
        dimensionLength: product?.dimensionLength || product?.dimensionlength || '',
        dimensionWidth: product?.dimensionWidth || product?.dimensionwidth || '',
        dimensionHeight: product?.dimensionHeight || product?.dimensionheight || '',
        pickupType: product?.pickupType || product?.pickuptype || PICKUP_TYPE.ON_SITE,
        pickupAddress: product?.pickupAddress || product?.pickupaddress || '',
        pickupLatitude: product?.pickupLatitude || product?.pickuplatitude || null,
        pickupLongitude: product?.pickupLongitude || product?.pickuplongitude || null,
        shippingAvailable: product?.shippingAvailable || product?.shippingavailable || false,
        shippingCost: product?.shippingCost || product?.shippingcost || '',
        externalUrl: product?.externalUrl || product?.externalurl || '',
        ownerType: product?.ownerType || product?.ownertype || OWNER_TYPE.PERSONAL,
        placeId: product?.placeId || product?.placeid || ''
    });

    // Load product types
    useEffect(() => {
        const loadTypes = async () => {
            setLoading(true);
            const types = await getProductTypes(language);
            setProductTypes(types);
            setLoading(false);
        };
        loadTypes();
    }, [language]);

    // Handle form change
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Handle photo upload
    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingPhoto(true);
        try {
            const userUuid = localStorage.getItem('userUuid');
            const userId = localStorage.getItem('userId');

            // Upload file
            const result = await uploadFile(file, userUuid, userId, 'PRODUCT_PHOTO');

            if (result.success) {
                const newPhoto = {
                    id: result.data.id || result.data.ID,
                    fileId: result.data.id || result.data.ID,
                    filePath: result.data.filePath || result.data.FILEPATH,
                    isMain: photos.length === 0,
                    aiEnhanced: false
                };
                setPhotos(prev => [...prev, newPhoto]);
            }
        } catch (error) {
            console.error('Error uploading photo:', error);
        }
        setUploadingPhoto(false);
    };

    // Handle remove photo
    const handleRemovePhoto = (index) => {
        setPhotos(prev => prev.filter((_, i) => i !== index));
    };

    // Handle set main photo
    const handleSetMainPhoto = (index) => {
        setPhotos(prev => prev.map((photo, i) => ({
            ...photo,
            isMain: i === index
        })));
    };

    // Open enhance modal
    const openEnhanceModal = (index) => {
        setShowEnhanceModal(index);
        setEnhancePrompt(tp.enhanceDefaultPrompt);
    };

    // Handle AI enhancement
    const handleEnhancePhoto = async () => {
        const index = showEnhanceModal;
        const photo = photos[index];
        if (!photo) return;

        setShowEnhanceModal(null);
        setEnhancingPhoto(index);

        try {
            const filePath = photo.filePath || photo.filepath || photo.FILEPATH;
            const fileUrl = getFileUrl(filePath);

            // Call AI enhancement service with custom prompt
            const result = await enhanceImageWithAI(fileUrl, {
                customPrompt: enhancePrompt,
                language
            });

            if (result.success && result.enhancedBase64) {
                // Convert base64 to blob and upload
                const userUuid = localStorage.getItem('userUuid');
                const userId = localStorage.getItem('userId');

                // Create file from base64
                const byteCharacters = atob(result.enhancedBase64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: result.mimeType });
                const file = new File([blob], 'enhanced.png', { type: result.mimeType });

                const uploadResult = await uploadFile(file, userUuid, userId, 'PRODUCT_PHOTO');

                if (uploadResult.success) {
                    setPhotos(prev => prev.map((p, i) => i === index ? {
                        ...p,
                        fileId: uploadResult.data.id || uploadResult.data.ID,
                        filePath: uploadResult.data.filePath || uploadResult.data.FILEPATH,
                        aiEnhanced: true
                    } : p));
                }
            } else {
                console.error('AI enhancement failed:', result.error);
                alert(result.error || 'Erreur lors de l\'amélioration');
            }
        } catch (error) {
            console.error('Error enhancing photo:', error);
            alert('Erreur lors de l\'amélioration de la photo');
        }
        setEnhancingPhoto(null);
    };

    // Handle submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.title.trim()) {
            alert(tp.errorTitle);
            return;
        }
        if (!formData.typeId) {
            alert(tp.errorCategory);
            return;
        }
        if (formData.listingType === LISTING_TYPE.SELL && !formData.price) {
            alert(tp.errorPrice);
            return;
        }

        setSaving(true);
        try {
            const userId = parseInt(localStorage.getItem('userId'), 10);

            const productData = {
                ...formData,
                peopleId: userId,
                price: formData.listingType === LISTING_TYPE.GIVE ? null : parseFloat(formData.price) || 0,
                quantity: parseInt(formData.quantity, 10) || 1,
                weight: parseFloat(formData.weight) || 0,
                dimensionLength: parseFloat(formData.dimensionLength) || 0,
                dimensionWidth: parseFloat(formData.dimensionWidth) || 0,
                dimensionHeight: parseFloat(formData.dimensionHeight) || 0,
                shippingCost: parseFloat(formData.shippingCost) || 0,
                placeId: formData.ownerType === OWNER_TYPE.BUSINESS ? parseInt(formData.placeId, 10) || 0 : 0
            };

            let result;
            if (isEditMode) {
                result = await updateProduct(product.productUuid || product.productuuid, productData);
            } else {
                result = await createProduct(productData);
            }

            if (result.success) {
                // Save photos if new product
                if (!isEditMode && result.data?.productUuid) {
                    for (const photo of photos) {
                        await addProductPhoto(result.data.productUuid, photo.fileId, photo.isMain);
                    }
                }

                onSave?.(result.data);
            } else {
                alert(result.message || 'Error saving product');
            }
        } catch (error) {
            console.error('Error saving product:', error);
            alert(error.message);
        }
        setSaving(false);
    };

    return (
        <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={onCancel}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900">
                    {isEditMode ? tp.editTitle : tp.createTitle}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Package className="w-5 h-5 text-cabaza-purple" />
                        {tp.basicInfo}
                    </h2>

                    <div className="space-y-4">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tp.title} *
                            </label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                placeholder={tp.titlePlaceholder}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                required
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tp.description}
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                placeholder={tp.descriptionPlaceholder}
                                rows={4}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent resize-none"
                            />
                        </div>

                        {/* Category */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tp.category} *
                            </label>
                            <select
                                value={formData.typeId}
                                onChange={(e) => handleChange('typeId', e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                required
                            >
                                <option value="">{tp.selectCategory}</option>
                                {productTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Photos */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Image className="w-5 h-5 text-cabaza-purple" />
                        {tp.photos}
                    </h2>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {/* Existing photos */}
                        {photos.map((photo, index) => (
                            <div key={index} className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 group">
                                <img
                                    src={getFileUrl(photo.filePath || photo.filepath || photo.FILEPATH)}
                                    alt={`Photo ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />

                                {/* Main badge */}
                                {photo.isMain && (
                                    <div className="absolute top-1 left-1 bg-cabaza-purple text-white text-xs px-2 py-0.5 rounded-full">
                                        {tp.mainPhoto}
                                    </div>
                                )}

                                {/* AI enhanced badge */}
                                {photo.aiEnhanced && (
                                    <div className="absolute top-1 right-1 bg-green-500 text-white p-1 rounded-full">
                                        <Sparkles className="w-3 h-3" />
                                    </div>
                                )}

                                {/* Actions overlay */}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                    {!photo.isMain && (
                                        <button
                                            type="button"
                                            onClick={() => handleSetMainPhoto(index)}
                                            className="p-2 bg-white rounded-full text-cabaza-purple hover:bg-gray-100"
                                            title={tp.setAsMain}
                                        >
                                            <Tag className="w-4 h-4" />
                                        </button>
                                    )}

                                    <button
                                            type="button"
                                            onClick={() => openEnhanceModal(index)}
                                            disabled={enhancingPhoto === index}
                                            className="p-2 bg-white rounded-full text-green-600 hover:bg-gray-100 disabled:opacity-50"
                                            title={tp.enhanceWithAI}
                                        >
                                            {enhancingPhoto === index ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Sparkles className="w-4 h-4" />
                                            )}
                                        </button>

                                    <button
                                        type="button"
                                        onClick={() => handleRemovePhoto(index)}
                                        className="p-2 bg-white rounded-full text-red-600 hover:bg-gray-100"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Enhancing overlay */}
                                {enhancingPhoto === index && (
                                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                                        <div className="text-center text-white">
                                            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                                            <span className="text-sm">{tp.enhancing}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Add photo button */}
                        <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-cabaza-purple hover:bg-purple-50 transition-colors">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                className="hidden"
                                disabled={uploadingPhoto}
                            />
                            {uploadingPhoto ? (
                                <Loader2 className="w-8 h-8 text-cabaza-purple animate-spin" />
                            ) : (
                                <>
                                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                    <span className="text-sm text-gray-500">{tp.addPhoto}</span>
                                </>
                            )}
                        </label>
                    </div>
                </div>

                {/* Pricing */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Euro className="w-5 h-5 text-cabaza-purple" />
                        {tp.pricing}
                    </h2>

                    <div className="space-y-4">
                        {/* Listing type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {tp.listingType}
                            </label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="listingType"
                                        value={LISTING_TYPE.SELL}
                                        checked={formData.listingType === LISTING_TYPE.SELL}
                                        onChange={(e) => handleChange('listingType', e.target.value)}
                                        className="text-cabaza-purple focus:ring-cabaza-purple"
                                    />
                                    <span>{tp.toSell}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="listingType"
                                        value={LISTING_TYPE.GIVE}
                                        checked={formData.listingType === LISTING_TYPE.GIVE}
                                        onChange={(e) => handleChange('listingType', e.target.value)}
                                        className="text-green-500 focus:ring-green-500"
                                    />
                                    <span>{tp.toGive}</span>
                                </label>
                            </div>
                        </div>

                        {/* Price (only if selling) */}
                        {formData.listingType === LISTING_TYPE.SELL && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {tp.price} *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={formData.price}
                                            onChange={(e) => handleChange('price', e.target.value)}
                                            min="0"
                                            step="0.01"
                                            className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                            required
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                                            {formData.currency}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-end">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.priceNegotiable}
                                            onChange={(e) => handleChange('priceNegotiable', e.target.checked)}
                                            className="rounded text-cabaza-purple focus:ring-cabaza-purple"
                                        />
                                        <span className="text-sm text-gray-700">{tp.negotiable}</span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Condition */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {tp.condition}
                            </label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="condition"
                                        value={PRODUCT_CONDITION.NEW}
                                        checked={formData.condition === PRODUCT_CONDITION.NEW}
                                        onChange={(e) => handleChange('condition', e.target.value)}
                                        className="text-blue-500 focus:ring-blue-500"
                                    />
                                    <span>{tp.new}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="condition"
                                        value={PRODUCT_CONDITION.USED}
                                        checked={formData.condition === PRODUCT_CONDITION.USED}
                                        onChange={(e) => handleChange('condition', e.target.value)}
                                        className="text-gray-500 focus:ring-gray-500"
                                    />
                                    <span>{tp.used}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Details */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Ruler className="w-5 h-5 text-cabaza-purple" />
                        {tp.details}
                    </h2>

                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            {/* Quantity */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {tp.quantity}
                                </label>
                                <input
                                    type="number"
                                    value={formData.quantity}
                                    onChange={(e) => handleChange('quantity', e.target.value)}
                                    min="1"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>

                            {/* Weight */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {tp.weight}
                                </label>
                                <input
                                    type="number"
                                    value={formData.weight}
                                    onChange={(e) => handleChange('weight', e.target.value)}
                                    min="0"
                                    step="0.1"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Dimensions */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {tp.dimensions}
                            </label>
                            <div className="grid grid-cols-3 gap-4">
                                <input
                                    type="number"
                                    value={formData.dimensionLength}
                                    onChange={(e) => handleChange('dimensionLength', e.target.value)}
                                    min="0"
                                    placeholder={tp.length}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                                <input
                                    type="number"
                                    value={formData.dimensionWidth}
                                    onChange={(e) => handleChange('dimensionWidth', e.target.value)}
                                    min="0"
                                    placeholder={tp.width}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                                <input
                                    type="number"
                                    value={formData.dimensionHeight}
                                    onChange={(e) => handleChange('dimensionHeight', e.target.value)}
                                    min="0"
                                    placeholder={tp.height}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* External URL */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <Link className="w-4 h-4 inline mr-1" />
                                {tp.externalUrl}
                            </label>
                            <input
                                type="url"
                                value={formData.externalUrl}
                                onChange={(e) => handleChange('externalUrl', e.target.value)}
                                placeholder={tp.externalUrlPlaceholder}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>

                {/* Pickup */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-cabaza-purple" />
                        {tp.pickup}
                    </h2>

                    <div className="space-y-4">
                        {/* Pickup type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {tp.pickupType}
                            </label>
                            <div className="flex flex-wrap gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="pickupType"
                                        value={PICKUP_TYPE.ON_SITE}
                                        checked={formData.pickupType === PICKUP_TYPE.ON_SITE}
                                        onChange={(e) => handleChange('pickupType', e.target.value)}
                                        className="text-cabaza-purple focus:ring-cabaza-purple"
                                    />
                                    <span>{tp.onSite}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="pickupType"
                                        value={PICKUP_TYPE.IN_STORE}
                                        checked={formData.pickupType === PICKUP_TYPE.IN_STORE}
                                        onChange={(e) => handleChange('pickupType', e.target.value)}
                                        className="text-cabaza-purple focus:ring-cabaza-purple"
                                    />
                                    <span>{tp.inStore}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="pickupType"
                                        value={PICKUP_TYPE.BOTH}
                                        checked={formData.pickupType === PICKUP_TYPE.BOTH}
                                        onChange={(e) => handleChange('pickupType', e.target.value)}
                                        className="text-cabaza-purple focus:ring-cabaza-purple"
                                    />
                                    <span>{tp.both}</span>
                                </label>
                            </div>
                        </div>

                        {/* Pickup address */}
                        <AddressAutocomplete
                            label={tp.pickupAddress}
                            value={formData.pickupAddress}
                            onChange={(field, value) => handleChange('pickupAddress', value)}
                            onCoordinatesChange={(lat, lng) => {
                                handleChange('pickupLatitude', lat);
                                handleChange('pickupLongitude', lng);
                            }}
                        />

                        {/* Shipping */}
                        <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.shippingAvailable}
                                    onChange={(e) => handleChange('shippingAvailable', e.target.checked)}
                                    className="rounded text-cabaza-purple focus:ring-cabaza-purple"
                                />
                                <span className="text-sm text-gray-700">{tp.shipping}</span>
                            </label>

                            {formData.shippingAvailable && (
                                <div className="flex-1 max-w-[200px]">
                                    <input
                                        type="number"
                                        value={formData.shippingCost}
                                        onChange={(e) => handleChange('shippingCost', e.target.value)}
                                        min="0"
                                        step="0.01"
                                        placeholder={tp.shippingCost}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Owner */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <User className="w-5 h-5 text-cabaza-purple" />
                        {tp.owner}
                    </h2>

                    <div className="space-y-4">
                        {/* Owner type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {tp.ownerType}
                            </label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="ownerType"
                                        value={OWNER_TYPE.PERSONAL}
                                        checked={formData.ownerType === OWNER_TYPE.PERSONAL}
                                        onChange={(e) => handleChange('ownerType', e.target.value)}
                                        className="text-cabaza-purple focus:ring-cabaza-purple"
                                    />
                                    <User className="w-4 h-4 text-gray-500" />
                                    <span>{tp.personal}</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="ownerType"
                                        value={OWNER_TYPE.BUSINESS}
                                        checked={formData.ownerType === OWNER_TYPE.BUSINESS}
                                        onChange={(e) => handleChange('ownerType', e.target.value)}
                                        className="text-cabaza-purple focus:ring-cabaza-purple"
                                    />
                                    <Building className="w-4 h-4 text-gray-500" />
                                    <span>{tp.business}</span>
                                </label>
                            </div>
                        </div>

                        {/* Select place if business */}
                        {formData.ownerType === OWNER_TYPE.BUSINESS && userPlaces.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {tp.selectPlace}
                                </label>
                                <select
                                    value={formData.placeId}
                                    onChange={(e) => handleChange('placeId', e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                >
                                    <option value="">{tp.selectPlace}</option>
                                    {userPlaces.map(place => (
                                        <option key={place.id} value={place.id}>
                                            {place.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-4">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onCancel}
                        disabled={saving}
                    >
                        {tp.cancel}
                    </Button>
                    <Button
                        type="submit"
                        disabled={saving}
                        icon={saving ? Loader2 : Save}
                        className={saving ? 'animate-pulse' : ''}
                    >
                        {saving ? tp.saving : tp.save}
                    </Button>
                </div>
            </form>

            {/* AI Enhancement Modal */}
            {showEnhanceModal !== null && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-green-500" />
                                    {tp.enhanceTitle}
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setShowEnhanceModal(null)}
                                    className="p-1 rounded-lg hover:bg-gray-100"
                                >
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            <textarea
                                value={enhancePrompt}
                                onChange={(e) => setEnhancePrompt(e.target.value)}
                                rows={4}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-gray-700"
                            />

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowEnhanceModal(null)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    {tp.cancel}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleEnhancePhoto}
                                    className="px-4 py-2 text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    {tp.apply}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductForm;
